// Lightweight pagination shell. shadcn's official Pagination is a thin wrapper
// over a <nav> with `aria-label="pagination"`; we replicate the structure
// without the link-based abstraction (we use plain buttons because the URL
// sync is handled by useSearchParams in the consuming component).
import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from './button';

interface PaginationProps extends React.HTMLAttributes<HTMLElement> {
  /** 1-based current page. */
  page: number;
  /** Total page count (>= 1). */
  totalPages: number;
  onPrev: () => void;
  onNext: () => void;
  /** Optional summary text for the right-hand side of the pager (e.g. "25 of 128"). */
  summary?: string;
  /** Loading flag — disables the prev/next buttons while a request is in flight. */
  loading?: boolean;
}

export function Pagination({
  className,
  page,
  totalPages,
  onPrev,
  onNext,
  summary,
  loading,
  ...props
}: PaginationProps): React.ReactNode {
  const canPrev = page > 1 && !loading;
  const canNext = page < totalPages && !loading;
  return (
    <nav
      aria-label="Pagination"
      className={cn('flex items-center justify-between gap-3', className)}
      {...props}
    >
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onPrev}
          disabled={!canPrev}
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
          <span>Prev</span>
        </Button>
        <span className="text-[13px] text-text-muted" aria-live="polite">
          Page {page} of {totalPages}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onNext}
          disabled={!canNext}
          aria-label="Next page"
        >
          <span>Next</span>
          <ChevronRight className="h-4 w-4" aria-hidden />
        </Button>
      </div>
      {summary !== undefined ? (
        <span className="text-[13px] text-text-muted">{summary}</span>
      ) : null}
    </nav>
  );
}
